/*
 * Smart Swimsuits — quick add, shared by the collection grid and the homepage
 * product rail.
 *
 * Markup contract:
 *   [data-quick]                 wrapper; gets .is-open / .is-busy
 *     [data-quick-toggle]        the "+" button
 *     [data-quick-size][data-variant-id]   one button per size
 *
 * Or, pick then add (the collection grid and the homepage rail): the sizes
 * are [data-quick-pick] buttons that only choose; the choice arms a single
 * [data-quick-add][data-quick-size] "Add to cart" button (its
 * [data-quick-add-size] shows the size) and the wrapper gets .is-chosen.
 *
 * The add is wired the way Dwell's product form does it: announce the add with
 * a CartLinesUpdateEvent, post it with the cart drawer's section ids, then
 * resolve the event with the fresh cart so the glass drawer re-renders and
 * opens. Loaded as a module, so it runs once however many sections include it.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const close = (quick) => {
  quick.classList.remove('is-open', 'is-chosen');
  quick.querySelector('[data-quick-toggle]')?.setAttribute('aria-expanded', 'false');
  quick.querySelectorAll('[data-quick-pick]').forEach((b) => b.setAttribute('aria-pressed', 'false'));
  const add = quick.querySelector('[data-quick-add]');
  if (add) add.disabled = true;
};

const closeAll = (except) => {
  document.querySelectorAll('[data-quick].is-open').forEach((q) => {
    if (q !== except) close(q);
  });
};

document.addEventListener('click', async (event) => {
  if (!(event.target instanceof Element)) return;

  const toggle = event.target.closest('[data-quick-toggle]');
  if (toggle) {
    const quick = toggle.closest('[data-quick]');
    const open = !quick.classList.contains('is-open');
    closeAll(quick);
    if (open) {
      quick.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
    } else {
      close(quick);
    }
    return;
  }

  const pick = event.target.closest('[data-quick-pick]');
  if (pick) {
    if (pick.disabled) return;
    const quick = pick.closest('[data-quick]');
    quick.querySelectorAll('[data-quick-pick]').forEach((b) => b.setAttribute('aria-pressed', String(b === pick)));
    const add = quick.querySelector('[data-quick-add]');
    if (add) {
      add.dataset.variantId = pick.dataset.variantId;
      add.disabled = false;
      const label = add.querySelector('[data-quick-add-size]');
      if (label) label.textContent = pick.textContent.trim();
    }
    quick.classList.add('is-chosen');
    return;
  }

  const btn = event.target.closest('[data-quick-size]');
  if (!btn) {
    if (!event.target.closest('[data-quick]')) closeAll();
    return;
  }
  if (btn.disabled) return;

  const quick = btn.closest('[data-quick]');
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);

  const body = new FormData();
  body.append('id', btn.dataset.variantId);
  body.append('quantity', '1');
  if (sectionIds.length) body.append('sections', sectionIds.join(','));

  const deferred = CartLinesUpdateEvent.createPromise();
  btn.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: btn.dataset.variantId, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  const root = window.Shopify?.routes?.root || '/';
  quick?.classList.add('is-busy');
  try {
    const res = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    }).then((r) => r.json());
    const cart = await fetch(`${root}cart.js`).then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        sections: res.sections,
        didError: Boolean(res.status),
        source: 'sss-quick-add',
      },
    });
  } catch (error) {
    deferred.reject(error);
  } finally {
    if (quick) {
      quick.classList.remove('is-busy');
      close(quick);
    }
  }
});
